/**
 * Community Realtime — React Hooks
 *
 * Phase 3: Supabase Realtime subscriptions for messages, presence, typing.
 *
 * ERRATA §4.3: Cache is InfiniteData<MessagePage>, NOT flat array.
 * ERRATA §4.2: client_temp_id reconciliation for sender's own messages.
 * ERRATA §4.5: reconcileOnReconnect uses fetchMessages API, not raw table.
 * ERRATA §4.6: canonical dot-notation event names.
 * ERRATA §4.7: out-of-order messages prepend to page 0.
 * ERRATA §6.5: presence_visible opt-out.
 *
 * @see BUILD_PLAN_INDEX — Phase 3 depends on Phases 1-2
 * @see phase-3-realtime-and-presence.md
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCurrentUser } from './hooks';
import { useUserActivity } from '@/hooks/useUserActivity';
import { communityKeys, applyUnread } from './community-hooks';
import { fetchMessages, type MessagePage, type MessageWithContext, type MemberProfile } from './community';
import {
  REALTIME_EVENTS,
  COMMUNITY_PROFILES_CHANNEL,
  type ProfileUpdatedPayload,
  type MessageCreatedPayload,
  type MessageUpdatedPayload,
  type MessageDeletedPayload,
  type ReactionAddedPayload,
  type ReactionRemovedPayload,
  type ReadUpdatedPayload,
  type ConversationUpdatedPayload,
  type NotificationCreatedPayload,
  type PresenceState,
  type TypingPresence,
} from './realtime-events';
import type { ConversationListItem } from './community';
import { FEED_NOTIFICATION_TYPES } from './client';
import type { Notification, NotificationPreferencesData } from './client';
import { toast } from 'sonner';
import { playNotificationSound } from '@/lib/notifications/sound';
import { showHostNotification } from '@/lib/notifications/host-notify';
import { normalizeNotificationUrl } from '@/lib/notifications/action-url';

interface NotifyArgs {
  title: string;
  body?: string;
  url?: string;
  tag?: string;
  soundEnabled: boolean;
  toastAction?: { label: string; onClick: () => void };
}

/**
 * Single entry point for all user-facing notifications. Plays the chime
 * (subject to prefs), tries an OS-level notification (granted + unfocused),
 * and falls back to a Sonner toast otherwise. Mute / pref gating is handled
 * by the caller — by the time we get here, the notification is meant to fire.
 */
async function notifyUser(args: NotifyArgs): Promise<void> {
  if (args.soundEnabled) playNotificationSound();
  const fired = await showHostNotification({
    title: args.title,
    body: args.body,
    url: args.url,
    tag: args.tag,
  });
  if (!fired) {
    toast(args.title, {
      description: args.body,
      duration: 5000,
      action: args.toastAction,
    });
  }
}

// ===========================================
// Active conversation tracking (module-level)
// Used by useUserChannel to suppress toasts for the conversation the user is viewing
// ===========================================

let _activeConversationId: string | null = null;

/** Call from Community.tsx when selectedConversation changes */
export function setActiveConversationId(id: string | null) {
  _activeConversationId = id;
}

/** The conversation the user is currently viewing — persisted by main.tsx even
 *  when it isn't in the top-10 warm set, so a refresh restores the open chat. */
export function getActiveConversationId(): string | null {
  return _activeConversationId;
}

/**
 * App-wide subscription to the community-profiles channel. When any member
 * changes their name/avatar (via `broadcastProfileUpdate`), patch the prewarmed
 * member map + the chat sender-profile overlay caches so the new avatar/name
 * shows IMMEDIATELY everywhere — including changes made on another device.
 * Mount once (Layout).
 */
export function useCommunityProfilesChannel() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!user) return;
    if (channelRef.current) return; // StrictMode guard

    // PRIVATE channel: the DB trigger on user_profiles is the only authorized
    // writer (realtime.send, server-side). Clients can receive (RLS SELECT) but
    // cannot broadcast a spoofed profile.updated. Requires the realtime auth
    // token to be set (supabase.realtime.setAuth) — handled on sign-in.
    const channel = supabase.channel(COMMUNITY_PROFILES_CHANNEL, {
      config: { private: true },
    });
    channelRef.current = channel;

    channel
      .on('broadcast', { event: REALTIME_EVENTS.PROFILE_UPDATED }, ({ payload }) => {
        const data = payload as unknown as ProfileUpdatedPayload;

        // 1) Prewarmed member map → instant reactor/mention/participant tooltips.
        queryClient.setQueryData<Map<string, MemberProfile>>(communityKeys.memberMap, (old) => {
          if (!old) return old;
          const next = new Map(old);
          next.set(data.user_id, {
            user_id: data.user_id,
            full_name: data.full_name,
            avatar_url: data.avatar_url,
          });
          return next;
        });

        // 2) Chat sender-profile overlay caches (['community','sender-profiles', ids]).
        queryClient.setQueriesData<Record<string, { full_name: string | null; avatar_url: string | null }>>(
          { queryKey: ['community', 'sender-profiles'] },
          (old) => {
            if (!old || !(data.user_id in old)) return old;
            return { ...old, [data.user_id]: { full_name: data.full_name, avatar_url: data.avatar_url } };
          },
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [user?.id, queryClient]);
}

// ===========================================
// useConversationRealtime
// ERRATA §4.3: operates on InfiniteData<MessagePage>
// ===========================================

/**
 * Subscribes to conversation:${conversationId} channel.
 * Handles message, reaction, and read receipt events in real time.
 * Updates TanStack Query cache directly (no refetch).
 */
export function useConversationRealtime(conversationId: string) {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  // Connection state tracking (previously lived in useReconnectionHandler).
  // Merged in so we don't need a duplicate `reconnect:${conversationId}` channel —
  // the main conversation channel already exposes the same SUBSCRIBED /
  // CHANNEL_ERROR / TIMED_OUT / CLOSED events via its subscribe callback.
  const [connectionState, setConnectionState] = useState<
    'connected' | 'reconnecting' | 'disconnected'
  >('connected');
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const reconcilingRef = useRef(false);
  // Bump this to force the useEffect below to tear down and recreate the channel,
  // e.g. when the user clicks a "retry" button in the connection banner.
  const [reconnectNonce, setReconnectNonce] = useState(0);

  const reconnect = useCallback(() => {
    setReconnectNonce((n) => n + 1);
  }, []);

  // Activity-driven auto-reconnect. Listeners only attach when the channel
  // is NOT in `connected` state — a healthy channel pays zero per-event
  // cost. When the channel drops we tap into useUserActivity (visibility /
  // online / focus / click / keydown / scroll / touch) and bump the nonce
  // on the first activity, with a 10s post-fire debounce inside the hook.
  const isDown =
    Boolean(conversationId && user) && connectionState !== 'connected';

  useUserActivity(
    () => {
      setReconnectNonce((n) => n + 1);
    },
    { enabled: isDown, throttleMs: 10_000 },
  );

  useEffect(() => {
    if (!conversationId || !user) return;

    let mounted = true;
    setConnectionState('connected');

    // PUBLIC join, deliberately — and it must stay public until the whole
    // conversation topic flips in one atomic change.
    //
    // Hermes migrations 468/473 open a PRIVATE leg of `conversation:<id>` and
    // dual-send `message.created` on both legs so old and new bundles overlap.
    // A client CANNOT sit on both legs: `supabase.channel(topic)` dedupes on
    // `realtime:${topic}` and returns the existing channel (params ignored), and
    // the socket itself multiplexes by topic — one join per topic per client.
    // So this is a choice of leg, not a choice of both.
    //
    // Public is the only correct choice today, because the private leg carries
    // NO event the public leg lacks: the trigger dual-sends message.created,
    // `conversation.updated` goes to the private `user:<id>` topic (not here),
    // and every other event on this topic (message.updated/deleted, reactions,
    // read.updated) is still CLIENT-sent — which lands on the public leg for
    // stale and current bundles alike. Joining private instead would silently
    // drop every edit, delete, reaction and read receipt exchanged with a bundle
    // on the other leg.
    //
    // Exit: the follow-up that removes the public leg must flip the join AND
    // every `broadcastToConversation` send to `private: true` together.
    const channel = supabase
      .channel(`conversation:${conversationId}`)

      // ─── message.created ───────────────────────────────
      // Server-emitted by trg_broadcast_message_events (hermes migration 468).
      // ERRATA §4.2: sender reconciles by client_temp_id
      // ERRATA §4.3: prepend to pages[0] of InfiniteData
      .on('broadcast', { event: REALTIME_EVENTS.MESSAGE_CREATED }, ({ payload }) => {
        const data = payload as unknown as MessageCreatedPayload;
        const msg = data.message;

        // Build MessageWithContext from broadcast payload
        const newMessage: MessageWithContext = {
          id: msg.id as string,
          conversation_id: msg.conversation_id as string,
          sender_id: msg.sender_id as string,
          sender: {
            full_name: data.sender_profile?.full_name ?? '',
            avatar_url: data.sender_profile?.avatar_url ?? null,
          },
          content: msg.content as string,
          message_type: msg.message_type ?? 'text',
          reply_to: msg.reply_to ?? null,
          attachments: (msg.attachments ?? []) as any[],
          mentions: (msg.mentions ?? []) as any[],
          metadata: msg.metadata ?? null,
          reactions: [],
          edited_at: msg.edited_at ?? null,
          deleted_at: msg.deleted_at ?? null,
          created_at: msg.created_at as string,
        };

        queryClient.setQueryData<InfiniteData<MessagePage>>(
          communityKeys.messages(conversationId),
          (old) => {
            if (!old) return old;

            // Dedup: the broadcast id matches the sender's optimistic id
            // (both are the same client-generated UUIDv7), so the sender
            // already has this row in cache. Refresh server-resolved
            // fields (sender profile, server-side metadata) and clear the
            // _optimistic flag, then bail.
            const optimisticIdx = old.pages.findIndex((p) =>
              p.messages.some((m) => m.id === msg.id),
            );
            if (optimisticIdx !== -1) {
              return {
                ...old,
                pages: old.pages.map((page, pIdx) =>
                  pIdx === optimisticIdx
                    ? {
                        ...page,
                        messages: page.messages.map((m) =>
                          m.id === msg.id ? newMessage : m,
                        ),
                      }
                    : page,
                ),
              };
            }

            // ERRATA §4.7: insert into page 0 maintaining DESC-id order so
            // that out-of-order broadcast delivery (network jitter — B's
            // broadcast arriving before A's, even though A was created
            // first) doesn't surface as wrong-order messages in the
            // rendered list. UUIDv7 ids are time-prefixed at ms precision,
            // so id-DESC == typed-order-DESC even under jitter.
            //
            // Skip leading optimistic rows (sender's own pending messages,
            // flagged via metadata._optimistic) so we don't shuffle them
            // away from the bottom — they get reconciled when their own
            // broadcast comes back.
            //
            // Cap page 0 at 500 messages to prevent unbounded memory
            // growth (Safari crash fix).
            const MAX_PAGE_MESSAGES = 500;
            const newPages = [...old.pages];
            const msgs = newPages[0].messages;
            let insertAt = 0;
            while (
              insertAt < msgs.length &&
              (msgs[insertAt].metadata as any)?._optimistic === true
            ) {
              insertAt++;
            }
            while (insertAt < msgs.length && msgs[insertAt].id > newMessage.id) {
              insertAt++;
            }
            const newMessages = [
              ...msgs.slice(0, insertAt),
              newMessage,
              ...msgs.slice(insertAt),
            ].slice(0, MAX_PAGE_MESSAGES);
            newPages[0] = {
              ...newPages[0],
              messages: newMessages,
            };
            return { ...old, pages: newPages };
          },
        );
      })

      // ─── message.updated ───────────────────────────────
      // ERRATA §4.3: iterate across all pages
      .on('broadcast', { event: REALTIME_EVENTS.MESSAGE_UPDATED }, ({ payload }) => {
        const data = payload as unknown as MessageUpdatedPayload;

        queryClient.setQueryData<InfiniteData<MessagePage>>(
          communityKeys.messages(conversationId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                messages: page.messages.map((m) =>
                  m.id === data.message_id
                    ? {
                        ...m,
                        content: data.content,
                        edited_at: data.edited_at,
                        ...(data.metadata != null ? { metadata: data.metadata } : {}),
                      }
                    : m,
                ),
              })),
            };
          },
        );
      })

      // ─── message.deleted ───────────────────────────────
      // ERRATA §5.7: soft delete clears content
      .on('broadcast', { event: REALTIME_EVENTS.MESSAGE_DELETED }, ({ payload }) => {
        const data = payload as unknown as MessageDeletedPayload;

        queryClient.setQueryData<InfiniteData<MessagePage>>(
          communityKeys.messages(conversationId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                messages: page.messages.map((m) =>
                  m.id === data.message_id
                    ? { ...m, deleted_at: data.deleted_at, content: '', attachments: [] }
                    : m,
                ),
              })),
            };
          },
        );
      })

      // ─── reaction.added ────────────────────────────────
      .on('broadcast', { event: REALTIME_EVENTS.REACTION_ADDED }, ({ payload }) => {
        const data = payload as unknown as ReactionAddedPayload;

        queryClient.setQueryData<InfiniteData<MessagePage>>(
          communityKeys.messages(conversationId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                messages: page.messages.map((m) => {
                  if (m.id !== data.message_id) return m;
                  const existing = m.reactions.find((r) => r.emoji === data.emoji);
                  if (existing) {
                    return {
                      ...m,
                      reactions: m.reactions.map((r) =>
                        r.emoji === data.emoji
                          ? {
                              ...r,
                              count: r.count + 1,
                              reacted_by_me: r.reacted_by_me || data.user_id === user.id,
                              user_ids:
                                (r.user_ids ?? []).includes(data.user_id) ||
                                (r.user_ids?.length ?? 0) >= 100
                                  ? (r.user_ids ?? [])
                                  : [...(r.user_ids ?? []), data.user_id],
                            }
                          : r,
                      ),
                    };
                  }
                  return {
                    ...m,
                    reactions: [...m.reactions, { emoji: data.emoji, count: 1, reacted_by_me: data.user_id === user.id, user_ids: [data.user_id] }],
                  };
                }),
              })),
            };
          },
        );
      })

      // ─── reaction.removed ──────────────────────────────
      .on('broadcast', { event: REALTIME_EVENTS.REACTION_REMOVED }, ({ payload }) => {
        const data = payload as unknown as ReactionRemovedPayload;

        queryClient.setQueryData<InfiniteData<MessagePage>>(
          communityKeys.messages(conversationId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                messages: page.messages.map((m) => {
                  if (m.id !== data.message_id) return m;
                  const existing = m.reactions.find((r) => r.emoji === data.emoji);
                  if (!existing) return m;
                  if (existing.count <= 1) {
                    return { ...m, reactions: m.reactions.filter((r) => r.emoji !== data.emoji) };
                  }
                  return {
                    ...m,
                    reactions: m.reactions.map((r) =>
                      r.emoji === data.emoji
                        ? {
                            ...r,
                            count: r.count - 1,
                            reacted_by_me: data.user_id === user.id ? false : r.reacted_by_me,
                            user_ids: (r.user_ids ?? []).filter((id) => id !== data.user_id),
                          }
                        : r,
                    ),
                  };
                }),
              })),
            };
          },
        );
      })

      // ─── read.updated ─────────────────────────────────
      // ERRATA §2.5: other participants' read pointers → "Seen" indicators.
      // This handler must NOT touch unread counts: READ_UPDATED is broadcast to
      // every viewer of the conversation, so invalidating the unread-counts cache
      // here fanned out to active-viewers × read-events refetches of the heavy
      // get_conversations_for_user RPC (a 2026-06-15 stampede residual). It was also
      // redundant — whoever read is either you (already cleared locally by
      // useMarkAsRead's applyUnread) or another participant (your count is unchanged).
      .on('broadcast', { event: REALTIME_EVENTS.READ_UPDATED }, ({ payload }) => {
        const data = payload as any;

        // Update participant's last_read_message_id in conversation detail cache — ERRATA §2.1
        if (data.user_id && data.last_read_message_id) {
          queryClient.setQueryData(
            communityKeys.conversation(conversationId),
            (old: any) => {
              if (!old) return old;
              return {
                ...old,
                participants: old.participants?.map((p: any) =>
                  p.user_id === data.user_id
                    ? { ...p, last_read_message_id: data.last_read_message_id }
                    : p,
                ),
              };
            },
          );
        }
      })

      .subscribe((status) => {
        if (!mounted) return;
        if (status === 'SUBSCRIBED') {
          setConnectionState('connected');
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = undefined;
          }
          // Reconcile cache on reconnect (ERRATA §4.5: fetchMessages API, not raw table)
          if (!reconcilingRef.current) {
            reconcilingRef.current = true;
            reconcileConversationMessages(conversationId, queryClient).finally(() => {
              reconcilingRef.current = false;
            });
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // 5s debounce before showing "reconnecting" — transient blips shouldn't flash UI
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            if (mounted) setConnectionState('reconnecting');
          }, 5_000);
        } else if (status === 'CLOSED') {
          setConnectionState('disconnected');
        }
      });

    // Visibility-change reconcile: when the tab becomes visible, refetch to
    // catch any messages that arrived while the WebSocket was throttled or
    // silently dead. Browsers throttle WS heartbeats on backgrounded tabs;
    // the connection may stay open in CONNECTED state but miss frames. The
    // existing SUBSCRIBED-after-disconnect path doesn't fire in that case
    // because there was no CLOSE event to react to.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && mounted && !reconcilingRef.current) {
        reconcilingRef.current = true;
        reconcileConversationMessages(conversationId, queryClient).finally(() => {
          reconcilingRef.current = false;
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = undefined;
      }
      document.removeEventListener('visibilitychange', onVisibility);
      supabase.removeChannel(channel);
    };
    // queryClient accessed via ref to avoid re-subscription on reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, user?.id, reconnectNonce]);

  return { connectionState, reconnect };
}

// ===========================================
// useUserChannel
// Per-user channel for sidebar updates
// ===========================================

/**
 * Subscribes to user:${userId} channel. Handles conversation.updated.
 */
export function useUserChannel() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Ensure notification preferences are cached for toast suppression checks
  useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) return null;
      return data as NotificationPreferencesData;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!user) return;

    // StrictMode guard: if channel already exists from a rapid remount, skip
    if (channelRef.current) return;

    // PRIVATE topic (migration 465): realtime authorization only consults the
    // realtime.messages receive policy for channels joined with private: true.
    // Public per-user topics were readable by anyone who knew the user id.
    const channel = supabase
      .channel(`user:${user.id}`, { config: { private: true } });
    channelRef.current = channel;

    channel

      // ─── conversation.updated — sidebar preview ────────
      // Server-emitted by trg_broadcast_message_events (hermes migration 468) to
      // this private topic. Unlike the client fan-out it replaced, the trigger
      // includes the SENDER (so their other tabs keep a synced sidebar), so this
      // handler has to do the self-filtering the emitter used to do.
      .on('broadcast', { event: REALTIME_EVENTS.CONVERSATION_UPDATED }, ({ payload }) => {
        const data = payload as unknown as ConversationUpdatedPayload;
        const isViewingConversation = _activeConversationId === data.conversation_id;
        // Own message: patch the preview, but never self-notify. Sending from a
        // surface that isn't the open chat (a feed share, a member intro, an
        // auto-share on a post mention) otherwise toasts you your own message and
        // increments your own unread. Guarded on an OPTIONAL field: emitters that
        // don't send sender_id behave exactly as before.
        const isOwnMessage = typeof data.sender_id === 'string' && data.sender_id === user.id;

        // A system line (hermes 647): a group was created, renamed, recoloured,
        // or someone was added, removed, promoted or left. Name, colour, member
        // count, role and left_at live only on the conversation rows, so refetch
        // them and the open thread. Every live member gets this, including the
        // person just removed and the person just re-added.
        if (data.message_type === 'system') {
          void queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
          void queryClient.invalidateQueries({ queryKey: communityKeys.conversation(data.conversation_id) });
          void queryClient.invalidateQueries({ queryKey: communityKeys.messages(data.conversation_id) });
        }

        // Update conversation preview (last message).
        queryClient.setQueriesData<ConversationListItem[]>(
          { queryKey: ['community', 'conversations'] },
          (old) => {
            if (!old) return old;
            return old.map((c) =>
              c.id === data.conversation_id
                ? {
                    ...c,
                    last_message_at: data.last_message_at,
                    last_message_preview: data.last_message_preview,
                    // The payload always carried the sender's name; not
                    // patching it left the previous sender's name glued to
                    // the new preview until the next refetch.
                    last_message_sender_name: data.sender_name,
                  }
                : c,
            );
          },
        );

        // Unread: increment locally instead of refetching get_conversations_for_user.
        // (2026-06-15 fix — the two invalidateQueries here fanned out to 2×N heavy-RPC
        // refetches per message and exhausted the connection pool.) Skip when the user
        // is viewing the conversation — it'll be marked read. Drift is corrected by the
        // throttled visibility-gated reconcile.
        if (!isViewingConversation && !isOwnMessage) {
          applyUnread(queryClient, data.conversation_id, { kind: 'increment' });
        }

        // Show toast — respect notification preferences (Phase 5 §3a)
        // Two mute systems coexist: (1) notification_preferences.muted_conversations jsonb array
        // for global user-level mute, and (2) conversation_participants.muted_until per-row
        // for per-channel mute (used by default-mute on join + ChatSettingsPanel). Either must suppress.
        //
        // The conversations query key is ['community', 'conversations', filters] — a 3-element
        // key. Use findAll to partial-match across all filter variants, since we don't know
        // which one currently holds the target conversation.
        const prefs = queryClient.getQueryData<NotificationPreferencesData>(['notificationPreferences']);
        const conversationQueries = queryClient.getQueryCache().findAll({ queryKey: ['community', 'conversations'] });
        let participantMuted = false;
        let conversationKnown = false;
        for (const q of conversationQueries) {
          const listData = q.state.data as ConversationListItem[] | undefined;
          const hit = listData?.find((c) => c.id === data.conversation_id);
          if (hit) {
            conversationKnown = true;
            participantMuted = hit.muted === true;
            break;
          }
        }

        // Brand-new conversation (e.g. an admin-brokered intro DM created for this
        // recipient): the map-only patch above can't add a row that isn't cached,
        // so refetch the list ONCE. Guarded to (a) an actually-cached list and
        // (b) unknown conversations only. The `length > 0` guard is critical: if
        // the conversations query has been GC'd while this user channel is still
        // live, EVERY message would resolve to unknown and refetch per-message —
        // the exact get_conversations_for_user storm the 2026-06-15 fix removed.
        // A GC'd/unmounted list will refetch fresh when it next mounts anyway.
        if (conversationQueries.length > 0 && !conversationKnown) {
          void queryClient.invalidateQueries({ queryKey: ['community', 'conversations'] });
        }
        const isMuted = prefs?.muted_conversations?.includes(data.conversation_id) || participantMuted;
        const hasMention = !!data.has_mention;
        // Mentions use their own preference toggle, DMs use direct_messages
        const notifyEnabled = hasMention
          ? (prefs?.mentions ?? true)
          : (prefs?.direct_messages ?? true);

        if (!isViewingConversation && !isOwnMessage && notifyEnabled && !isMuted && data.sender_name && data.last_message_preview) {
          void notifyUser({
            title: hasMention ? `${data.sender_name} mentioned you` : data.sender_name,
            body: data.last_message_preview,
            url: '/community',
            tag: `conversation:${data.conversation_id}`,
            soundEnabled: prefs?.sound_enabled ?? true,
          });
        }
      })

      // unread.changed (ERRATA §2.5 cross-tab sync) was never wired: nothing —
      // client or server — has ever emitted it, so the listener was dead weight
      // and the "drift is corrected by the cross-tab UNREAD_CHANGED event" claim
      // above it was false. Cross-tab drift is corrected by the visibility-gated
      // reconcile instead. Removed with the event name and its payload schema.

      // notification.created is handled by useNotificationRealtime on the
      // dedicated `user:${userId}:notifications` channel. Backend currently
      // broadcasts to both channels for legacy reasons — listening here too
      // would fire OS notifications twice for every event.

      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}

// ===========================================
// useNotificationRealtime
// App-wide notification listener — mount in Layout
// Phase 5 §8a, ERRATA §4.6
// ===========================================

/**
 * Subscribes to user:${userId}:notifications channel for app-wide
 * notification events. Mount in Layout so toasts work on all pages.
 * Safe to run alongside useUserChannel (separate channel name).
 */
export function useNotificationRealtime() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      // PRIVATE topic (migration 465) — see useUserChannel.
      .channel(`user:${user.id}:notifications`, { config: { private: true } })

      .on('broadcast', { event: REALTIME_EVENTS.NOTIFICATION_CREATED }, ({ payload }) => {
        const data = payload as unknown as NotificationCreatedPayload;

        // Bell-count partition: plain chat messages live in the watermark
        // unread model (sidebar badge / pills / tab title) and are excluded
        // from the bell count at the query layer — the optimistic increment
        // must match, or every chat message inflates the bell until the next
        // refetch. Mentions/replies DO count (they are bell items).
        const isBellType = data.type !== 'community.message';

        if (isBellType) {
          queryClient.setQueryData<number>(
            ['unreadNotificationCount'],
            (old) => (old ?? 0) + 1,
          );
        }

        // Invalidate notification list
        queryClient.invalidateQueries({ queryKey: ['notifications'] });

        // Feed pill badge — bump only for feed-type notifications so the
        // Feed tab in /community reflects new activity without a refetch.
        // Shares the one list api.getUnreadFeedCount queries with, so the
        // optimistic bump and the authoritative count can never disagree about
        // what "feed activity" means.
        const isFeedType = (FEED_NOTIFICATION_TYPES as readonly string[]).includes(data.type);
        if (isFeedType) {
          queryClient.setQueryData<number>(
            ['unreadFeedCount'],
            (old) => (old ?? 0) + 1,
          );
        }

        // Skip OS-notify for chat-derived notifications — useUserChannel's
        // conversation.updated handler already fired a richer notification
        // (with sender name + preview) for these. We still update the inbox
        // unread count and cache above so the bell badge stays correct.
        const isChatType =
          data.type === 'community.message' ||
          data.type === 'community.mention' ||
          data.type === 'community.reply';

        const isOnInbox = window.location.pathname === '/inbox';
        if (!isOnInbox && !isChatType) {
          const prefs = queryClient.getQueryData<NotificationPreferencesData>(['notificationPreferences']);
          void notifyUser({
            title: data.title,
            body: data.message ?? undefined,
            // Land on the notification's own target — clicking "you were
            // mentioned in a comment" must open that comment, not /inbox.
            url: data.action_url ? normalizeNotificationUrl(data.action_url) : '/inbox',
            tag: `notification:${data.entity_id ?? data.title}`,
            soundEnabled: prefs?.sound_enabled ?? true,
          });
        }
      })

      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}

// ===========================================
// usePresence — global online status
// ERRATA §6.5: respects presence_visible
// ===========================================

export interface UserPresence {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  status: 'online' | 'idle' | 'offline';
}

// Tiered heartbeat timing (Phase 2 of realtime-memory-fixes-2026-04-12.md).
// Previously every input event (mousedown/keydown/scroll/touchstart) called
// channel.track() twice, which on iOS WebKit pushed WebContent memory past the
// highwater limit. Now:
//   - First interaction after idle → immediate track()
//   - Subsequent interactions within 30s → single debounced track() at 30s mark
//   - visibilitychange → visible → immediate track() (bypass debounce)
//   - 5 minutes of no interaction → track() with status='idle' (unchanged)
const PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;
const PRESENCE_IDLE_MS = 5 * 60 * 1000;

/**
 * Global presence subscription. Returns a map of user_id → UserPresence.
 * Multi-device: if any device is online, user shows online (Phase 3 §4).
 */
export function usePresence() {
  const { data: user } = useCurrentUser();
  const [onlineUsers, setOnlineUsers] = useState<Map<string, UserPresence>>(new Map());
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedTrackRef = useRef<ReturnType<typeof setTimeout>>();
  const lastTrackRef = useRef<number>(0);
  const channelRef = useRef<ReturnType<typeof supabase.channel>>();

  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel('presence:community');
    channelRef.current = channel;

    const trackOnline = () => {
      lastTrackRef.current = Date.now();
      channel.track({
        user_id: user.id,
        display_name: user.full_name || 'Unknown',
        avatar_url: user.avatar_url || null,
        status: 'online',
        active_conversation_id: null,
        last_seen: new Date().toISOString(),
      });
    };

    const trackIdle = () => {
      channel.track({
        user_id: user.id,
        display_name: user.full_name || 'Unknown',
        avatar_url: user.avatar_url || null,
        status: 'idle',
        active_conversation_id: null,
        last_seen: new Date().toISOString(),
      });
    };

    // Schedule (or reset) the 5-minute idle timer.
    const armIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(trackIdle, PRESENCE_IDLE_MS);
    };

    // Tiered heartbeat. Called on every interaction event but rate-limited to
    // at most 1 track() per PRESENCE_HEARTBEAT_INTERVAL_MS.
    const onInteraction = () => {
      armIdleTimer(); // any interaction postpones idle
      const now = Date.now();
      const sinceLast = now - lastTrackRef.current;

      if (sinceLast >= PRESENCE_HEARTBEAT_INTERVAL_MS) {
        // Immediate: first interaction after a quiet window
        if (debouncedTrackRef.current) {
          clearTimeout(debouncedTrackRef.current);
          debouncedTrackRef.current = undefined;
        }
        trackOnline();
      } else if (!debouncedTrackRef.current) {
        // Schedule a single deferred track at the next heartbeat slot
        const delay = PRESENCE_HEARTBEAT_INTERVAL_MS - sinceLast;
        debouncedTrackRef.current = setTimeout(() => {
          debouncedTrackRef.current = undefined;
          trackOnline();
        }, delay);
      }
      // else: a debounced track is already pending — do nothing
    };

    // Visibility change: when tab becomes visible again, flip to online
    // immediately (bypass the debounce) so friends see the user reappear.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (debouncedTrackRef.current) {
          clearTimeout(debouncedTrackRef.current);
          debouncedTrackRef.current = undefined;
        }
        trackOnline();
        armIdleTimer();
      }
    };

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<PresenceState>();
        const resolved = new Map<string, UserPresence>();

        // Multi-device resolution: best status wins
        for (const [_key, entries] of Object.entries(state)) {
          for (const entry of entries as PresenceState[]) {
            const existing = resolved.get(entry.user_id);
            if (!existing || betterStatus(entry.status, existing.status)) {
              resolved.set(entry.user_id, {
                user_id: entry.user_id,
                display_name: entry.display_name,
                avatar_url: entry.avatar_url,
                status: entry.status,
              });
            }
          }
        }

        setOnlineUsers(resolved);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          trackOnline();
          armIdleTimer();
        }
      });

    // Listen for user interaction (passive so we don't block scrolling)
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onInteraction, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = undefined;
      }
      if (debouncedTrackRef.current) {
        clearTimeout(debouncedTrackRef.current);
        debouncedTrackRef.current = undefined;
      }
      events.forEach((e) => window.removeEventListener(e, onInteraction));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return onlineUsers;
}

/** online > idle > offline */
function betterStatus(a: string, b: string): boolean {
  const rank: Record<string, number> = { online: 3, idle: 2, offline: 1 };
  return (rank[a] ?? 0) > (rank[b] ?? 0);
}

// ===========================================
// useTyping — typing indicators via Presence
// Phase 3 §5
// ===========================================

export interface UseTypingReturn {
  typingUsers: { user_id: string; display_name: string }[];
  sendTypingStart: () => void;
  sendTypingStop: () => void;
}

/**
 * Sends and receives typing events on conversation channel via Presence.
 * Disabled for channels with > 50 participants (Phase 3 §5).
 */
export function useTyping(conversationId: string, participantCount?: number): UseTypingReturn {
  const { data: user } = useCurrentUser();
  const [typingUsers, setTypingUsers] = useState<{ user_id: string; display_name: string }[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const channelRef = useRef<ReturnType<typeof supabase.channel>>();
  // Tracks whether we've already sent is_typing:true to avoid spamming presence on every keystroke.
  const isTrackingTypingRef = useRef(false);

  // Disable typing for large channels (Phase 3 §5, §9)
  const isDisabled = (participantCount ?? 0) > 50;

  useEffect(() => {
    if (!conversationId || !user || isDisabled) return;

    // We reuse the conversation channel for typing presence
    // The conversation channel is already subscribed by useConversationRealtime,
    // so we create a separate presence-only channel for typing
    const channel = supabase.channel(`typing:${conversationId}`);
    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<TypingPresence>();
        const typing: { user_id: string; display_name: string }[] = [];

        for (const [_key, entries] of Object.entries(state)) {
          for (const entry of entries as TypingPresence[]) {
            // Filter self and only include currently typing
            if (entry.user_id !== user.id && entry.is_typing) {
              typing.push({ user_id: entry.user_id, display_name: entry.display_name });
            }
          }
        }

        setTypingUsers(typing);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          isTrackingTypingRef.current = false;
          await channel.track({
            user_id: user.id,
            display_name: user.full_name || 'Unknown',
            is_typing: false,
          });
        }
      });

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = undefined;
      }
      isTrackingTypingRef.current = false;
      channelRef.current = undefined;
      supabase.removeChannel(channel);
    };
  }, [conversationId, user?.id, isDisabled]);

  // Send is_typing:true immediately on first keystroke, then auto-stop after 3s of silence.
  // The guard flag prevents spamming presence on every keystroke — only the first call
  // per typing burst actually calls track().
  const sendTypingStart = useCallback(() => {
    if (isDisabled || !channelRef.current || !user) return;

    // Only track if not already marked as typing
    if (!isTrackingTypingRef.current) {
      isTrackingTypingRef.current = true;
      channelRef.current?.track({
        user_id: user.id,
        display_name: user.full_name || 'Unknown',
        is_typing: true,
      });
    }

    // Reset auto-stop timer on every keystroke
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = undefined;
    }
    typingTimeoutRef.current = setTimeout(() => {
      typingTimeoutRef.current = undefined;
      isTrackingTypingRef.current = false;
      channelRef.current?.track({
        user_id: user.id,
        display_name: user.full_name || 'Unknown',
        is_typing: false,
      });
    }, 3000);
  }, [user, isDisabled]);

  const sendTypingStop = useCallback(() => {
    if (isDisabled || !channelRef.current || !user) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = undefined;
    }
    isTrackingTypingRef.current = false;
    channelRef.current?.track({
      user_id: user.id,
      display_name: user.full_name || 'Unknown',
      is_typing: false,
    });
  }, [user, isDisabled]);

  return { typingUsers, sendTypingStart, sendTypingStop };
}

// ===========================================
// reconcileOnReconnect
// ERRATA §4.5: uses fetchMessages API on reconnect
// ===========================================
//
// Formerly called from a dedicated `useReconnectionHandler` hook with a
// separate `reconnect:${conversationId}` channel. That hook was merged into
// `useConversationRealtime` (see Phase 1 of realtime-memory-fixes-2026-04-12.md)
// to eliminate one channel per active conversation — the main conversation
// channel's `.subscribe((status) => ...)` callback fires the same events.

// Throttle the conversations-list refetch triggered by reconnect/visibility
// reconciles. That query hits the heavy get_conversations_for_user RPC; a
// flapping connection or rapid foreground/background toggling could otherwise
// fire it on every SUBSCRIBED/visibilitychange event (a 2026-06-15 stampede
// contributor). Leading throttle: the first call in a window refreshes,
// subsequent calls are skipped — drift is bounded and corrected next window.
let lastConversationsReconcileAt = 0;
const CONVERSATIONS_RECONCILE_THROTTLE_MS = 30_000;

function maybeRefreshConversations(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  const now = Date.now();
  if (now - lastConversationsReconcileAt < CONVERSATIONS_RECONCILE_THROTTLE_MS) return;
  lastConversationsReconcileAt = now;
  queryClient.invalidateQueries({ queryKey: communityKeys.conversations() });
}

// Reconcile bookkeeping, keyed by conversation. Two guards, because opening a
// conversation triggers two callers a few hundred ms apart — ChatView's mount-time
// reconcile and the channel's SUBSCRIBED callback:
//   inFlight  — collapses them when they overlap (shared promise).
//   lastAt    — collapses them when they DON'T. reconcileOnReconnect calls
//               fetchMessages directly, outside React Query, so nothing else
//               dedupes it; without this, every conversation open costs two full
//               page-0 fetches (plus two reactions batches) on the hot path.
// The cooldown is short enough that a genuine reconnect or foreground reconcile
// (always well beyond it) still gets through.
const inFlightReconciles = new Map<string, Promise<void>>();
const lastReconciledAt = new Map<string, number>();
const RECONCILE_COOLDOWN_MS = 3_000;

/**
 * One-shot "catch up on anything sent while we weren't listening" for a
 * conversation's message cache. Deduped per conversation.
 *
 * This is the ONLY path that surfaces messages sent while the app was
 * backgrounded: `useMessages` is staleTime:Infinity + persisted to IndexedDB so
 * mounting never refetches, and realtime broadcasts are not replayed on reconnect.
 * Callable independently of the realtime channel — see the mount-time call in
 * ChatView, which covers the case where the socket hasn't reconnected yet.
 */
export function reconcileConversationMessages(
  conversationId: string,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  const existing = inFlightReconciles.get(conversationId);
  if (existing) return existing;
  const last = lastReconciledAt.get(conversationId) ?? 0;
  if (Date.now() - last < RECONCILE_COOLDOWN_MS) return Promise.resolve();
  const run = reconcileOnReconnect(conversationId, queryClient).finally(() => {
    lastReconciledAt.set(conversationId, Date.now());
    inFlightReconciles.delete(conversationId);
  });
  inFlightReconciles.set(conversationId, run);
  return run;
}

/**
 * Canonical, order-insensitive fingerprint of a message's reactions.
 *
 * Server-side aggregation order follows row order, which is not stable between
 * fetches, so a naive deep-compare would report a change on every reconcile for
 * a thread nobody touched. Emoji and reactor ids are both sorted here so only a
 * REAL difference (new emoji, changed count, someone added/removed) registers.
 */
function reactionsFingerprint(
  reactions: MessageWithContext['reactions'] | undefined,
): string {
  if (!reactions || reactions.length === 0) return '';
  return reactions
    .map(
      (r) =>
        `${r.emoji}:${r.count}:${r.reacted_by_me ? 1 : 0}:${[...(r.user_ids ?? [])].sort().join(',')}`,
    )
    .sort()
    .join('|');
}

/**
 * Patch one cached message with the server's current IN-PLACE state.
 *
 * "In-place state" is exactly the field set the in-place realtime broadcasts
 * mutate — reaction.added/removed → `reactions`; message.updated → `content`,
 * `edited_at`, `metadata`; message.deleted → `deleted_at`, `content`,
 * `attachments`. Everything else about a message is immutable once written.
 *
 * Reconcile has to heal these itself. `useMessages` is staleTime:Infinity and
 * persisted to IndexedDB, so an already-cached page is NEVER refetched on
 * mount — if the broadcast carrying one of these edits arrived while we weren't
 * subscribed (app backgrounded, socket down, another conversation open), no
 * other path repairs it. Until 2026-08 reconcile fetched a fresh page 0,
 * reactions and all, then discarded every row it already held, so a reaction
 * left on an older message stayed invisible to that client until the 24h gcTime
 * finally evicted the cache. The notification still fired (server-side, a
 * different path entirely), so the thread and the notification disagreed.
 *
 * Returns the ORIGINAL object when nothing differs. Reconcile runs on every
 * SUBSCRIBED and every foreground; handing React Query fresh identities for an
 * unchanged thread would re-render the whole view and re-serialize it to
 * IndexedDB each time.
 */
function mergeInPlaceState(
  cachedMsg: MessageWithContext,
  serverMsg: MessageWithContext,
): MessageWithContext {
  const changed =
    cachedMsg.content !== serverMsg.content ||
    cachedMsg.edited_at !== serverMsg.edited_at ||
    cachedMsg.deleted_at !== serverMsg.deleted_at ||
    reactionsFingerprint(cachedMsg.reactions) !==
      reactionsFingerprint(serverMsg.reactions);

  if (!changed) return cachedMsg;

  return {
    ...cachedMsg,
    reactions: serverMsg.reactions,
    content: serverMsg.content,
    edited_at: serverMsg.edited_at,
    deleted_at: serverMsg.deleted_at,
    // Carried with the delete/edit that triggered the change — a soft delete
    // clears both server-side, so taking them from the server row keeps the
    // tombstone consistent instead of leaving orphaned attachments behind.
    attachments: serverMsg.attachments,
    metadata: serverMsg.metadata,
  };
}

/**
 * Apply `mergeInPlaceState` across every cached page, preserving page and
 * message identity wherever nothing changed (see the note there). Returns the
 * original `pages` array when the whole cache is already current.
 */
function patchPagesInPlace(
  pages: MessagePage[],
  serverById: Map<string, MessageWithContext>,
): MessagePage[] {
  let anyPageChanged = false;

  const nextPages = pages.map((page) => {
    let pageChanged = false;

    const nextMessages = page.messages.map((m) => {
      const serverMsg = serverById.get(m.id);
      if (!serverMsg) return m;
      const merged = mergeInPlaceState(m, serverMsg);
      if (merged !== m) pageChanged = true;
      return merged;
    });

    if (!pageChanged) return page;
    anyPageChanged = true;
    return { ...page, messages: nextMessages };
  });

  return anyPageChanged ? nextPages : pages;
}

/**
 * Fetch messages newer than the latest cached one, and refresh the in-place
 * state (reactions / edits / deletes) of the ones we already have.
 * ERRATA §4.5: must use fetchMessages API, not raw table query.
 */
async function reconcileOnReconnect(
  conversationId: string,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  const cached = queryClient.getQueryData<InfiniteData<MessagePage>>(
    communityKeys.messages(conversationId),
  );
  if (!cached || cached.pages.length === 0) return;

  // Latest cached message is in page 0 (newest first from API, reversed for display)
  const allMessages = cached.pages.flatMap((p) => p.messages);
  if (allMessages.length === 0) return;

  // Newest cached message id (UUIDv7 string compare == chronological order).
  // Reconcile only injects messages STRICTLY NEWER than this — anything older
  // belongs to page 1+ territory and would break cross-page DESC invariants if
  // lumped into page 0. Mid-cache gaps from prior realtime drops stay until
  // the user scrolls up (page 1+ cursor fetch will surface them).
  const cachedNewestId = allMessages.reduce(
    (a, b) => (a.id > b.id ? a : b),
  ).id;

  try {
    // ERRATA §4.5: use fetchMessages, not raw table
    const PAGE_SIZE = 50;
    const newPage = await fetchMessages(conversationId, undefined, PAGE_SIZE);

    if (newPage.messages.length > 0) {
      // existingIds drives gap detection (overlap signal, do NOT tighten here).
      const existingIds = new Set(allMessages.map((m) => m.id));
      const overlapNewMessages = newPage.messages.filter(
        (m) => !existingIds.has(m.id),
      );

      // Gap detection: if the fetched page is at max capacity AND every row
      // is new (zero overlap with the cache), there's almost certainly a
      // hole between what we just fetched and what we already have. One
      // page can't bridge that — anything older than newPage's oldest row
      // and newer than the cache's latest is invisibly missing.
      // Safer to drop the cache and refetch from scratch than to render an
      // incomplete stream the user can't see is incomplete.
      const isPageFull = newPage.messages.length === PAGE_SIZE;
      const allFetchedAreNew =
        overlapNewMessages.length === newPage.messages.length;
      if (isPageFull && allFetchedAreNew) {
        queryClient.invalidateQueries({
          queryKey: communityKeys.messages(conversationId),
        });
        // Also refresh conversations sidebar before returning early (throttled).
        maybeRefreshConversations(queryClient);
        return;
      }

      // Tighten to messages strictly newer than the cached newest before
      // merging — never reach into page 1+ territory from page 0.
      const messagesToPrepend = overlapNewMessages.filter(
        (m) => m.id > cachedNewestId,
      );

      // Rows we ALREADY hold are not discarded: the same fetch carries their
      // current reactions/edits/deletes, and this is the only path that can
      // heal them (see mergeInPlaceState). Keyed by id so page 1+ rows that
      // fall inside the fetched window get healed too, not just page 0.
      const serverById = new Map(newPage.messages.map((m) => [m.id, m]));

      queryClient.setQueryData<InfiniteData<MessagePage>>(
        communityKeys.messages(conversationId),
        (old) => {
          if (!old) return old;

          // Heal in place FIRST, so a message that is both patched and kept
          // lands in one cache write rather than two renders.
          const patchedPages = patchPagesInPlace(old.pages, serverById);

          // Re-deduplicate against CURRENT cache — MESSAGE_CREATED may have
          // inserted some of these while fetchMessages was in flight.
          const currentIds = new Set(
            patchedPages.flatMap((p) => p.messages.map((m) => m.id)),
          );
          const deduped = messagesToPrepend.filter((m) => !currentIds.has(m.id));

          if (deduped.length === 0) {
            // Identity preserved by patchPagesInPlace when nothing changed —
            // returning `old` itself keeps React Query from notifying.
            return patchedPages === old.pages ? old : { ...old, pages: patchedPages };
          }

          const newPages = [...patchedPages];
          const merged = [...deduped, ...newPages[0].messages]
            .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
          newPages[0] = { ...newPages[0], messages: merged };
          return { ...old, pages: newPages };
        },
      );
    }
  } catch {
    // On failure, invalidate to trigger full refetch
    queryClient.invalidateQueries({
      queryKey: communityKeys.messages(conversationId),
    });
  }

  // Refresh conversations list (unread counts may have changed) — throttled so a
  // flapping reconnect/visibility loop can't stampede get_conversations_for_user.
  maybeRefreshConversations(queryClient);
}

// ===========================================
// Formatting helpers
// ===========================================

/**
 * Format typing indicator text (Phase 3 §5).
 */
export function formatTypingText(typingUsers: { display_name: string }[]): string | null {
  if (typingUsers.length === 0) return null;
  if (typingUsers.length === 1) return `${typingUsers[0].display_name} is typing`;
  if (typingUsers.length === 2) return `${typingUsers[0].display_name} and ${typingUsers[1].display_name} are typing`;
  return `${typingUsers.length} people are typing`;
}
